import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, EnvelopeSimple, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AuthActionShell, { authButtonClass, authInputClass } from "@/components/AuthActionShell";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState((params.get("email") || "").trim().toLowerCase());
  const [status, setStatus] = useState(token ? "verifying" : "waiting");
  const [message, setMessage] = useState(
    token
      ? "Verifying your email..."
      : email
        ? `We sent a verification email to ${email}. Open it and tap “Verify email address” to continue.`
        : "Check your inbox and open the Callsect verification email to continue."
  );
  const attempted = useRef(false);
  const redirectTimerRef = useRef(null);

  useEffect(() => () => {
    if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
  }, []);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api.post("/auth/verify-email", { token })
      .then(({ data }) => {
        if (!data?.access_token) throw new Error("Verification completed without a session token.");
        localStorage.setItem("cs_token", data.access_token);
        if (data?.email) setEmail(data.email);
        setStatus("success");
        setMessage(data?.message || "Email verified. Signing you in...");
        redirectTimerRef.current = window.setTimeout(() => {
          window.location.replace("/dashboard");
        }, 650);
      })
      .catch((error) => {
        setStatus("error");
        const detail = error?.response?.data?.detail;
        setMessage(typeof detail === "string" ? detail : "This verification link could not be used.");
      });
  }, [token]);

  const resend = async (event) => {
    event?.preventDefault?.();
    if (!email) return;
    setStatus("sending");
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setStatus("waiting");
      setMessage(
        data?.message ||
          `A new verification email was sent to ${email}. Open that email to continue.`
      );
    } catch {
      setStatus("waiting");
      setMessage("If that account still needs verification, a new link has been sent.");
    }
  };

  const statusClass = status === "success"
    ? "is-success"
    : status === "error"
      ? "is-error"
      : "is-neutral";

  const waitingForInbox = status === "waiting" || status === "sending" || status === "error";

  return (
    <AuthActionShell
      eyebrow="Email verification"
      title={status === "success" ? "You’re verified" : status === "error" ? "Verification link expired" : "Check your email"}
      description={
        status === "success"
          ? "Your Callsect account is active. We’re taking you straight into the app."
          : status === "error"
            ? "Send yourself a fresh verification link below, then open it to continue."
            : "Open the verification email from Callsect. Don’t sign in yet: once you verify, you’ll be taken straight into the app."
      }
      showSignIn={false}
      brandTo="/"
    >
      <div className={`auth-action-status ${statusClass}`} role="status" aria-live="polite">
        {status === "success"
          ? <CheckCircle size={19} weight="fill" />
          : status === "error"
            ? <WarningCircle size={19} weight="fill" />
            : <EnvelopeSimple size={19} weight="duotone" />}
        <span>{message}</span>
      </div>

      {waitingForInbox && email && (
        <button
          type="button"
          onClick={resend}
          disabled={status === "sending"}
          className={`${authButtonClass} auth-action-primary-button--secondary`}
        >
          {status === "sending" ? "Sending..." : "Resend verification email"}
        </button>
      )}

      {waitingForInbox && !email && (
        <form onSubmit={resend} className="auth-action-form auth-action-form--spaced">
          <label className="auth-action-label">
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
              className={authInputClass}
            />
          </label>
          <button type="submit" disabled={status === "sending"} className={authButtonClass}>
            {status === "sending" ? "Sending..." : "Send a new verification link"}
          </button>
        </form>
      )}

      {status === "error" && email && (
        <Link
          to={`/login?mode=login&email=${encodeURIComponent(email)}`}
          className="auth-action-text-link"
        >
          Already verified? Return to sign in →
        </Link>
      )}
    </AuthActionShell>
  );
}
